import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatRelative, parseBackendJson } from "@/lib/format";
import { useToast } from "@/lib/toast";
import { CheckCheck, Check, Bell, BellOff } from "lucide-react";
import { useState } from "react";

interface NotifRow {
  id: string;
  type: string;
  contact_id: string | null;
  conversation_id: string | null;
  payload: Record<string, unknown> | null;
  is_read: number;
  created_at: string;
  read_at: string | null;
}

export function NotificationsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showRead, setShowRead] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["notifications", showRead],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "100" });
      if (!showRead) params.set("unread", "true");
      return api.get<NotifRow[]>(`/api/notifications?${params.toString()}`);
    },
    refetchInterval: 15_000,
  });

  const ackMut = useMutation({
    mutationFn: (id: string) => api.post(`/api/notifications/${id}/ack`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const ackAllMut = useMutation({
    mutationFn: () => api.post(`/api/notifications/ack-all`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      toast.success("All notifications marked read");
    },
    onError: (e) =>
      toast.error("Could not mark all read", e instanceof Error ? e.message : undefined),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-500">
            System alerts and AI escalations. Refreshes every 15 seconds.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showRead ? "primary" : "outline"}
            size="sm"
            onClick={() => setShowRead(!showRead)}
          >
            {showRead ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            {showRead ? "Show unread only" : "Show all"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => ackAllMut.mutate()}
            disabled={ackAllMut.isPending}
          >
            <CheckCheck className="h-4 w-4" />
            Mark all read
          </Button>
        </div>
      </header>

      <Card>
        {isLoading ? (
          <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">
            {showRead ? "No notifications yet." : "All caught up - no unread notifications."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((n) => (
              <li
                key={n.id}
                className={`flex items-start gap-3 px-4 py-3 ${
                  n.is_read === 0 ? "bg-blue-50/30" : "bg-brand-surface"
                }`}
              >
                <span
                  className={`mt-1.5 h-2 w-2 rounded-full ${
                    n.is_read === 0 ? "bg-blue-500" : "bg-slate-300"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={notifVariant(n.type)}>{n.type}</Badge>
                    <span className="text-xs text-slate-400">
                      {formatRelative(n.created_at)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 font-mono">
                    {n.conversation_id && (
                      <span className="mr-3">conv: {n.conversation_id.slice(-8)}</span>
                    )}
                    {n.contact_id && <span>contact: {n.contact_id.slice(-8)}</span>}
                  </div>
                  {n.payload && (
                    <pre className="mt-2 text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1 overflow-x-auto">
                      {JSON.stringify(parseBackendJson(n.payload), null, 0)}
                    </pre>
                  )}
                </div>
                {n.is_read === 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => ackMut.mutate(n.id)}
                    disabled={ackMut.isPending}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function notifVariant(type: string): "default" | "info" | "warning" | "danger" | "success" {
  if (type.includes("very_hot")) return "danger";
  if (type.includes("hot")) return "warning";
  if (type.includes("escalation") || type.includes("complaint")) return "danger";
  if (type === "marketplace_lead") return "info";
  if (type === "channel_disconnected") return "danger";
  return "default";
}
